import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardCheck,
  ExternalLink,
  FileCheck2,
  FileText,
  Loader2,
  LockKeyhole,
  Map,
  Plane,
  Radio,
  Save,
  ShieldCheck,
  TentTree,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import {
  formatServiceNumber,
  useTrainingSession,
} from "../../context/TrainingSessionContext";

const PDF_PATH = "/training-assets/first-class-logbook.pdf";

const field = (
  key,
  label,
  options = {}
) => ({
  key,
  label,
  multiline: options.multiline ?? true,
  rows: options.rows ?? 3,
  placeholder: options.placeholder ?? "",
});

const listFields = (
  prefix,
  count,
  label = "Answer"
) =>
  Array.from(
    { length: count },
    (_, index) =>
      field(
        `${prefix}${index + 1}`,
        `${label} ${index + 1}`,
        { multiline: false }
      )
  );

const pairFields = (
  prefix,
  count,
  left,
  right
) =>
  Array.from(
    { length: count },
    (_, index) => [
      field(
        `${prefix}${index + 1}a`,
        `${left} ${index + 1}`,
        { multiline: false }
      ),
      field(
        `${prefix}${index + 1}b`,
        `${right} ${index + 1}`
      ),
    ]
  ).flat();

export const FIRST_CLASS_SECTIONS = [
  {
    id: "flying",
    title: "Flying Operations",
    icon: Plane,
    pages: "3–19",
    description:
      "Airfields, Air Cadet aircraft, glider launching, lookout and the phonetic alphabet.",
  },
  {
    id: "map",
    title: "Map Reading",
    icon: Map,
    pages: "20–30, 53–54",
    description:
      "Map types and symbols, grid references, land features, contour lines and route planning.",
  },
  {
    id: "expedition",
    title: "Initial Expedition Training",
    icon: TentTree,
    pages: "31–41",
    description:
      "Planning, equipment, camp craft, food safety, hypothermia and heat illness.",
  },
  {
    id: "radio",
    title: "Basic Radio Communications",
    icon: Radio,
    pages: "42–52",
    description:
      "Radio equipment, networks, procedures, message handling and radio safety.",
  },
];

export const FIRST_CLASS_TASKS = [
  // -----------------------------------------------------------------------
  // FLYING OPERATIONS
  // -----------------------------------------------------------------------
  {
    id: "flying-p1-a1",
    section: "flying",
    code: "P1",
    level: "pass",
    mandatory: true,
    page: 4,
    title: "Runway layouts",
    prompt:
      "Describe the three runway layouts shown in the source logbook, including the main use, advantages and disadvantages of each.",
    fields: [
      ...pairFields("r1", 1, "Runway type 1 – main use", "Runway type 1 – advantages"),
      field("r1dis", "Runway type 1 – disadvantages"),
      ...pairFields("r2", 1, "Runway type 2 – main use", "Runway type 2 – advantages"),
      field("r2dis", "Runway type 2 – disadvantages"),
      ...pairFields("r3", 1, "Runway type 3 – main use", "Runway type 3 – advantages"),
      field("r3dis", "Runway type 3 – disadvantages"),
    ],
  },
  {
    id: "flying-p2-a1",
    section: "flying",
    code: "P2",
    level: "pass",
    mandatory: true,
    page: 7,
    title: "Main features of an airfield",
    prompt:
      "Use the numbered airfield diagram on the source page and identify features 1 to 16.",
    fields: listFields("feature", 16, "Feature"),
  },
  {
    id: "flying-p2-a2",
    section: "flying",
    code: "P2",
    level: "pass",
    mandatory: true,
    page: 8,
    title: "Purpose of airfield features",
    prompt:
      "Describe what each listed airfield feature is used for.",
    fields: [
      field("controlTower", "Control Tower"),
      field("hangars", "Hangars"),
      field("windsock", "Windsock"),
      field("identificationBeacon", "Identification Beacon"),
      field(
        "dispersal",
        "Aircraft Servicing Platform / Dispersal Area"
      ),
    ],
  },
  {
    id: "flying-m1-a1",
    section: "flying",
    code: "M1",
    level: "merit",
    mandatory: false,
    page: 9,
    title: "Runway markings",
    prompt:
      "Identify each labelled runway marking and explain its purpose.",
    fields: pairFields(
      "marking",
      6,
      "Marking name",
      "Purpose"
    ),
  },
  {
    id: "flying-m2",
    section: "flying",
    code: "M2",
    level: "merit",
    mandatory: false,
    page: 10,
    title: "Airfield lighting",
    prompt:
      "Identify the four groups of runway lights shown and describe the purpose of each.",
    fields: pairFields(
      "light",
      4,
      "Light name",
      "Purpose"
    ),
  },
  {
    id: "flying-d1",
    section: "flying",
    code: "D1",
    level: "distinction",
    mandatory: false,
    page: 11,
    title: "Evaluate airfield designs",
    prompt:
      "Evaluate strengths and weaknesses of three airfield designs and their features.",
    fields: [
      ...listFields("strength", 3, "Strengths – airfield type"),
      ...listFields("weakness", 3, "Weaknesses – airfield type"),
      field(
        "conclusion",
        "Conclusion and reasons for your view",
        { rows: 5 }
      ),
    ],
  },
  {
    id: "flying-p3",
    section: "flying",
    code: "P3",
    level: "pass",
    mandatory: true,
    page: 12,
    title: "Air Cadet aircraft",
    prompt:
      "Identify the two aircraft shown and describe their main use and key features.",
    fields: [
      field("aircraft1name", "Aircraft 1 – name", { multiline: false }),
      field("aircraft1use", "Aircraft 1 – main use"),
      field("aircraft1features", "Aircraft 1 – key features"),
      field("aircraft2name", "Aircraft 2 – name", { multiline: false }),
      field("aircraft2use", "Aircraft 2 – main use"),
      field("aircraft2features", "Aircraft 2 – key features"),
    ],
  },
  {
    id: "flying-p4",
    section: "flying",
    code: "P4",
    level: "pass",
    mandatory: true,
    page: 13,
    title: "Glider control surfaces",
    prompt:
      "Identify the four control surfaces marked on the glider diagram.",
    fields: listFields("surface", 4, "Control surface"),
  },
  {
    id: "flying-m3",
    section: "flying",
    code: "M3",
    level: "merit",
    mandatory: false,
    page: 14,
    title: "Flight instruments",
    prompt:
      "Identify each instrument shown and briefly explain its purpose.",
    fields: pairFields(
      "instrument",
      5,
      "Instrument name",
      "Purpose"
    ),
  },
  {
    id: "flying-d2",
    section: "flying",
    code: "D2",
    level: "distinction",
    mandatory: false,
    page: 15,
    title: "Control surface effects",
    prompt:
      "For each of the three movements shown, name the movement, describe what happens and identify the control surface involved.",
    fields: Array.from(
      { length: 3 },
      (_, index) => [
        field(
          `effect${index + 1}movement`,
          `Effect ${index + 1} – movement`,
          { multiline: false }
        ),
        field(
          `effect${index + 1}happens`,
          `Effect ${index + 1} – what happens?`
        ),
        field(
          `effect${index + 1}surface`,
          `Effect ${index + 1} – control surface`,
          { multiline: false }
        ),
      ]
    ).flat(),
  },
  {
    id: "flying-p5",
    section: "flying",
    code: "P5",
    level: "pass",
    mandatory: true,
    page: 16,
    title: "Winch launching a glider",
    prompt:
      "Name the three commands used during a winch launch and describe what happens for each command.",
    fields: pairFields(
      "command",
      3,
      "Command",
      "What happens?"
    ),
  },
  {
    id: "flying-p6",
    section: "flying",
    code: "P6",
    level: "pass",
    mandatory: true,
    page: 17,
    title: "The clock code",
    prompt:
      "Describe how the clock code is used to report the position of another aircraft.",
    fields: [
      field(
        "clockCode",
        "How would you use the clock code?",
        { rows: 6 }
      ),
    ],
  },
  {
    id: "flying-m4-a1",
    section: "flying",
    code: "M4",
    level: "merit",
    mandatory: false,
    page: 17,
    title: "Why lookout matters",
    prompt:
      "State three reasons why it is important to keep a good lookout for other aircraft.",
    fields: listFields("reason", 3, "Reason"),
  },
  {
    id: "flying-m4-a2",
    section: "flying",
    code: "M4",
    level: "merit",
    mandatory: false,
    page: 18,
    title: "Clock-code hazards",
    prompt:
      "For each aircraft shown in the yellow circle, report its position and explain the potential danger.",
    fields: pairFields(
      "hazard",
      3,
      "Position",
      "Potential danger"
    ),
  },
  {
    id: "flying-p7-a1",
    section: "flying",
    code: "P7",
    level: "pass",
    mandatory: true,
    page: 19,
    title: "Phonetic alphabet",
    prompt:
      "Enter the phonetic word for each letter A–Z and the spoken form of zero.",
    fields: [
      ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("").map(
        (letter) =>
          field(
            `phonetic${letter}`,
            letter,
            { multiline: false }
          )
      ),
      field("phonetic0", "0", { multiline: false }),
    ],
  },
  {
    id: "flying-p7-a2",
    section: "flying",
    code: "P7",
    level: "pass",
    mandatory: true,
    page: 19,
    title: "Practical phonetic alphabet exercise",
    prompt:
      "Repeat a simple message using the phonetic alphabet. This is a practical activity to be checked by an instructor.",
    practical: true,
  },

  // -----------------------------------------------------------------------
  // MAP READING
  // -----------------------------------------------------------------------
  {
    id: "map-p1-a1",
    section: "map",
    code: "P1",
    level: "pass",
    mandatory: true,
    page: 21,
    title: "Types and uses of maps",
    prompt:
      "Match different types of maps to their uses. This activity uses the assessment material supplied by your instructor.",
    practical: true,
  },
  {
    id: "map-p2-a1",
    section: "map",
    code: "P2",
    level: "pass",
    mandatory: true,
    page: 21,
    title: "Ordnance Survey symbols quiz",
    prompt:
      "Complete the 1:50,000 and 1:25,000 map-symbol quiz provided by your instructor.",
    practical: true,
  },
  {
    id: "map-p2-a2",
    section: "map",
    code: "P2",
    level: "pass",
    mandatory: true,
    page: 21,
    referencePages: [21, 22],
    title: "Identify OS map symbols",
    prompt:
      "Identify the 13 Ordnance Survey symbols shown on pages 21–22.",
    fields: listFields("symbol", 13, "Symbol"),
  },
  {
    id: "map-m1-a1",
    section: "map",
    code: "M1",
    level: "merit",
    mandatory: false,
    page: 23,
    title: "Map scales",
    prompt:
      "Identify the scale of each map shown, explain its purpose and why the scale matters for that role.",
    fields: listFields(
      "scale",
      3,
      "Map"
    ),
  },
  {
    id: "map-m1-a2",
    section: "map",
    code: "M1",
    level: "merit",
    mandatory: false,
    page: 24,
    title: "Importance of map symbols",
    prompt:
      "Using a 1:25,000 or 1:50,000 OS map, explain to your instructor why symbols are important for route planning, locating position and finding your way.",
    practical: true,
  },
  {
    id: "map-p3-a1",
    section: "map",
    code: "P3",
    level: "pass",
    mandatory: true,
    page: 24,
    title: "Practical grid references",
    prompt:
      "Complete the practical grid-referencing exercises provided by your instructor.",
    practical: true,
  },
  {
    id: "map-p3-a2",
    section: "map",
    code: "P3",
    level: "pass",
    mandatory: true,
    page: 24,
    referencePages: [24, 53],
    title: "Six-figure grid references",
    prompt:
      "Using the Potters Bar map, find the six-figure grid references for the 14 indicated locations.",
    fields: listFields("gridref", 14, "Grid reference"),
  },
  {
    id: "map-p4-a1",
    section: "map",
    code: "P4",
    level: "pass",
    mandatory: true,
    page: 25,
    title: "Main land features",
    prompt:
      "With your instructor, identify the main land features on an OS map and describe or point out what they look like.",
    practical: true,
  },
  {
    id: "map-p4-a2",
    section: "map",
    code: "P4",
    level: "pass",
    mandatory: true,
    page: 25,
    referencePages: [25, 54],
    title: "Relief features",
    prompt:
      "Using the Rhum map, name relief features 1 to 5.",
    fields: listFields("relief", 5, "Feature"),
  },
  {
    id: "map-m2-d1",
    section: "map",
    code: "M2 / D1",
    level: "distinction",
    mandatory: false,
    page: 26,
    title: "Plan routes using land features",
    prompt:
      "Plan two or more accurate and achievable routes using OS map land features, and explain why land features matter for at least one route.",
    practical: true,
  },
  {
    id: "map-p5",
    section: "map",
    code: "P5",
    level: "pass",
    mandatory: true,
    page: 26,
    referencePages: [26, 54],
    title: "Contour lines and route planning",
    prompt:
      "Use the Rhum map to answer the four contour-line questions in the source workbook.",
    fields: listFields("contour", 4, "Answer"),
  },
  {
    id: "map-m3-a1",
    section: "map",
    code: "M3",
    level: "merit",
    mandatory: false,
    page: 27,
    title: "Slope gradient calculation",
    prompt:
      "Calculate the gradient where the vertical interval is 90 metres and the horizontal equivalent is 2 kilometres.",
    fields: [field("answer", "Answer", { multiline: false })],
  },
  {
    id: "map-m3-a2",
    section: "map",
    code: "M3",
    level: "merit",
    mandatory: false,
    page: 27,
    title: "Compare gradients",
    prompt:
      "Identify which of the listed gradients is the steepest.",
    fields: [field("answer", "Answer", { multiline: false })],
  },
  {
    id: "map-m3-a3",
    section: "map",
    code: "M3",
    level: "merit",
    mandatory: false,
    page: 27,
    title: "Gradient as ratio and percentage",
    prompt:
      "Calculate the gradient between points A and B on the diagram and show it as both a ratio and percentage.",
    fields: [field("answer", "Answer")],
  },
  {
    id: "map-d2",
    section: "map",
    code: "D2",
    level: "distinction",
    mandatory: false,
    page: 28,
    referencePages: [28, 29, 30],
    title: "Analyse land profiles",
    prompt:
      "Complete the land-profile projection exercises on pages 28–30. Several activities require drawing cross-sections, so complete those with your instructor and use the notes box for your written answers.",
    fields: [
      field(
        "writtenAnswers",
        "Written answers for Activities 2, 3, 5, 6, 8, 9, 11, 12, 14 and 15",
        { rows: 10 }
      ),
    ],
    practical: true,
    allowNotesWithPractical: true,
  },

  // -----------------------------------------------------------------------
  // INITIAL EXPEDITION TRAINING
  // -----------------------------------------------------------------------
  {
    id: "expedition-p1",
    section: "expedition",
    code: "P1",
    level: "pass",
    mandatory: true,
    page: 32,
    title: "Planning a walking expedition",
    prompt:
      "Identify four factors that should be considered at the initial planning stage of an expedition.",
    fields: listFields("factor", 4, "Factor"),
  },
  {
    id: "expedition-m1",
    section: "expedition",
    code: "M1",
    level: "merit",
    mandatory: false,
    page: 32,
    title: "Why planning factors matter",
    prompt:
      "Explain why each of the four planning factors identified in P1 is important.",
    fields: listFields("importance", 4, "Explanation"),
  },
  {
    id: "expedition-p2",
    section: "expedition",
    code: "P2",
    level: "pass",
    mandatory: true,
    page: 33,
    title: "Country Code",
    prompt:
      "List the five main points of the Country Code.",
    fields: listFields("countryCode", 5, "Point"),
  },
  {
    id: "expedition-p3",
    section: "expedition",
    code: "P3",
    level: "pass",
    mandatory: true,
    page: 33,
    title: "Route card",
    prompt:
      "Complete a route card for an imaginary or actual expedition and explain verbally why each part is important.",
    practical: true,
  },
  {
    id: "expedition-p4",
    section: "expedition",
    code: "P4",
    level: "pass",
    mandatory: true,
    page: 33,
    title: "Prepare equipment and clothing",
    prompt:
      "Demonstrate how expedition equipment and clothing can be prepared for the planned walk.",
    practical: true,
  },
  {
    id: "expedition-m2",
    section: "expedition",
    code: "M2",
    level: "merit",
    mandatory: false,
    page: 34,
    title: "Expedition safety equipment",
    prompt:
      "Identify expedition safety equipment and explain its use and importance.",
    fields: [
      field(
        "equipment",
        "Equipment, use and importance",
        { rows: 8 }
      ),
    ],
  },
  {
    id: "expedition-d1",
    section: "expedition",
    code: "D1",
    level: "distinction",
    mandatory: false,
    page: 34,
    title: "Evaluate expedition safety",
    prompt:
      "Identify safety considerations for a walking expedition and explain why each is important.",
    fields: [
      field(
        "safety",
        "Safety considerations and why they matter",
        { rows: 8 }
      ),
    ],
  },
  {
    id: "expedition-p5",
    section: "expedition",
    code: "P5",
    level: "pass",
    mandatory: true,
    page: 35,
    title: "Erect a tent",
    prompt:
      "Demonstrate that you can erect a tent as part of a practical exercise.",
    practical: true,
  },
  {
    id: "expedition-p6",
    section: "expedition",
    code: "P6",
    level: "pass",
    mandatory: true,
    page: 35,
    title: "Pack a rucksack",
    prompt:
      "Demonstrate methods of kit packing by packing a rucksack for an expedition.",
    practical: true,
  },
  {
    id: "expedition-m3",
    section: "expedition",
    code: "M3",
    level: "merit",
    mandatory: false,
    page: 35,
    title: "Use tents and carry equipment",
    prompt:
      "Use a tent and rucksack appropriately during a practical expedition activity.",
    practical: true,
  },
  {
    id: "expedition-d2",
    section: "expedition",
    code: "D2",
    level: "distinction",
    mandatory: false,
    page: 36,
    title: "Choose equipment for the situation",
    prompt:
      "Justify why particular tents and carry equipment are needed in specific expedition circumstances. Give examples.",
    fields: [
      field(
        "justification",
        "Your explanation and examples",
        { rows: 7 }
      ),
    ],
  },
  {
    id: "expedition-p7",
    section: "expedition",
    code: "P7",
    level: "pass",
    mandatory: true,
    page: 36,
    title: "Warmth and comfort in camp",
    prompt:
      "Identify four things that can improve warmth and/or comfort in camp.",
    fields: listFields("warmth", 4, "Point"),
  },
  {
    id: "expedition-p8-a1",
    section: "expedition",
    code: "P8",
    level: "pass",
    mandatory: true,
    page: 37,
    title: "Siting, pitching and striking camp",
    prompt:
      "Identify three important factors for siting a camp, three for pitching a tent and three for striking camp.",
    fields: [
      ...listFields("siting", 3, "Siting factor"),
      ...listFields("pitching", 3, "Pitching factor"),
      ...listFields("striking", 3, "Striking factor"),
    ],
  },
  {
    id: "expedition-p8-a2",
    section: "expedition",
    code: "P8",
    level: "pass",
    mandatory: true,
    page: 37,
    title: "Practical camp craft",
    prompt:
      "Practise the essentials of siting, pitching and striking a camp.",
    practical: true,
  },
  {
    id: "expedition-m4",
    section: "expedition",
    code: "M4",
    level: "merit",
    mandatory: false,
    page: 38,
    title: "Safe camp craft",
    prompt:
      "Explain how the principles of safe camp craft can be applied to an expedition.",
    fields: [
      field(
        "answer",
        "Your explanation",
        { rows: 7 }
      ),
    ],
  },
  {
    id: "expedition-p9",
    section: "expedition",
    code: "P9",
    level: "pass",
    mandatory: true,
    page: 38,
    title: "Food and cooking safety",
    prompt:
      "Give a verbal explanation and demonstration of safe food and cooking preparation for expeditions.",
    practical: true,
  },
  {
    id: "expedition-p10-a1",
    section: "expedition",
    code: "P10",
    level: "pass",
    mandatory: true,
    page: 39,
    title: "Symptoms of hypothermia",
    prompt:
      "Explain seven symptoms of hypothermia.",
    fields: listFields("symptom", 7, "Symptom"),
  },
  {
    id: "expedition-p10-a2",
    section: "expedition",
    code: "P10",
    level: "pass",
    mandatory: true,
    page: 40,
    title: "Symptoms of heat exhaustion",
    prompt:
      "Explain eight symptoms of heat exhaustion.",
    fields: listFields("symptom", 8, "Symptom"),
  },
  {
    id: "expedition-p10-a3",
    section: "expedition",
    code: "P10",
    level: "pass",
    mandatory: true,
    page: 40,
    title: "Symptoms of heat stroke",
    prompt:
      "Explain four symptoms of heat stroke.",
    fields: listFields("symptom", 4, "Symptom"),
  },
  {
    id: "expedition-p11-a1",
    section: "expedition",
    code: "P11",
    level: "pass",
    mandatory: true,
    page: 41,
    title: "Treating hypothermia",
    prompt:
      "Describe five actions for treating hypothermia in addition to seeking immediate medical help.",
    fields: listFields("action", 5, "Action"),
  },
  {
    id: "expedition-p11-a2",
    section: "expedition",
    code: "P11",
    level: "pass",
    mandatory: true,
    page: 41,
    title: "Treating heat exhaustion",
    prompt:
      "Describe two actions for treating heat exhaustion in addition to seeking immediate medical help.",
    fields: listFields("action", 2, "Action"),
  },
  {
    id: "expedition-p11-a3",
    section: "expedition",
    code: "P11",
    level: "pass",
    mandatory: true,
    page: 41,
    title: "Treating heat stroke",
    prompt:
      "Describe two actions for treating heat stroke in addition to seeking immediate medical help.",
    fields: listFields("action", 2, "Action"),
  },

  // -----------------------------------------------------------------------
  // BASIC RADIO COMMUNICATIONS
  // -----------------------------------------------------------------------
  {
    id: "radio-p1",
    section: "radio",
    code: "P1",
    level: "pass",
    mandatory: true,
    page: 43,
    title: "Types of radio equipment",
    prompt:
      "For a base set, vehicle installation and handheld radio, describe the main use, average range and an advantage or disadvantage.",
    fields: [
      ...["Base set", "Vehicle installation", "Handheld radio"].flatMap(
        (name, index) => [
          field(`radio${index + 1}use`, `${name} – main use`),
          field(`radio${index + 1}range`, `${name} – average range`, { multiline: false }),
          field(`radio${index + 1}advantage`, `${name} – advantage or disadvantage`),
        ]
      ),
    ],
  },
  {
    id: "radio-p2",
    section: "radio",
    code: "P2",
    level: "pass",
    mandatory: true,
    page: 43,
    title: "Operational frequency",
    prompt:
      "Complete the verbal assessment on how a radio's operational frequency can limit its use.",
    practical: true,
  },
  {
    id: "radio-p3",
    section: "radio",
    code: "P3",
    level: "pass",
    mandatory: true,
    page: 44,
    title: "Aerial height and transmission range",
    prompt:
      "Identify which of the three aerial locations are suited to short, medium and long-range communications, then state your conclusion about aerial height and transmission range.",
    fields: [
      ...listFields("location", 3, "Location"),
      field(
        "conclusion",
        "Conclusion about aerial height and transmission range",
        { rows: 5 }
      ),
    ],
  },
  {
    id: "radio-p4",
    section: "radio",
    code: "P4",
    level: "pass",
    mandatory: true,
    page: 45,
    title: "Air Cadet radio networks",
    prompt:
      "Use the network diagrams to describe each network, who controls it, who sends messages and whether stations can communicate directly.",
    fields: [
      field("network1", "Network 1 – answers", { rows: 7 }),
      field("network2", "Network 2 – answers", { rows: 7 }),
    ],
  },
  {
    id: "radio-m1",
    section: "radio",
    code: "M1",
    level: "merit",
    mandatory: false,
    page: 46,
    title: "Frequency and aerial length",
    prompt:
      "Explain the relationship between frequency and aerial length.",
    fields: [field("answer", "Explanation", { rows: 6 })],
  },
  {
    id: "radio-m2",
    section: "radio",
    code: "M2",
    level: "merit",
    mandatory: false,
    page: 46,
    title: "Callsign construction",
    prompt:
      "Explain how an Air Cadet unit callsign is constructed and how a mobile callsign is formed from a base callsign.",
    fields: [
      field("unitCallsign", "Your unit callsign", { multiline: false }),
      field("mMeaning", "Meaning of M"),
      field("rMeaning", "Meaning of R"),
      field("mobile", "What is added to indicate a mobile callsign?"),
    ],
  },
  {
    id: "radio-d1",
    section: "radio",
    code: "D1",
    level: "distinction",
    mandatory: false,
    page: 47,
    title: "Choosing a control station",
    prompt:
      "Evaluate the factors that influence the choice of a control station.",
    fields: [
      field(
        "factors",
        "Factors and comments",
        { rows: 9 }
      ),
    ],
  },
  {
    id: "radio-p5",
    section: "radio",
    code: "P5",
    level: "pass",
    mandatory: true,
    page: 48,
    title: "Phonetic alphabet and figures",
    prompt:
      "Undertake a verbal test spelling several words and conveying numeric sequences using correct radio pronunciation.",
    practical: true,
  },
  {
    id: "radio-p6",
    section: "radio",
    code: "P6",
    level: "pass",
    mandatory: true,
    page: 48,
    title: "Radio checks",
    prompt:
      "Undertake a simulated verbal test using correct procedures to obtain radio checks.",
    practical: true,
  },
  {
    id: "radio-m3",
    section: "radio",
    code: "M3",
    level: "merit",
    mandatory: false,
    page: 49,
    title: "Convey messages",
    prompt:
      "Undertake a verbal test using correct procedures and messaging techniques.",
    practical: true,
  },
  {
    id: "radio-d2",
    section: "radio",
    code: "D2",
    level: "distinction",
    mandatory: false,
    page: 49,
    title: "Relay messages",
    prompt:
      "Undertake a verbal test relaying messages to stations out of direct range using correct prowords and network-control principles.",
    practical: true,
  },
  {
    id: "radio-p7",
    section: "radio",
    code: "P7",
    level: "pass",
    mandatory: true,
    page: 50,
    title: "Radio batteries",
    prompt:
      "Practically demonstrate, or discuss with your instructor, the safe care, handling and disposal of radio batteries.",
    practical: true,
  },
  {
    id: "radio-p8",
    section: "radio",
    code: "P8",
    level: "pass",
    mandatory: true,
    page: 50,
    title: "Portable radio procedures",
    prompt:
      "Practically demonstrate, or discuss with your instructor, correct procedures when using portable radio equipment.",
    practical: true,
  },
  {
    id: "radio-p9",
    section: "radio",
    code: "P9",
    level: "pass",
    mandatory: true,
    page: 51,
    title: "Radio health and safety",
    prompt:
      "Identify health and safety issues associated with radio operations and the safety action you would take for each.",
    fields: pairFields(
      "issue",
      5,
      "Issue",
      "Safety action"
    ),
  },
  {
    id: "radio-m4",
    section: "radio",
    code: "M4",
    level: "merit",
    mandatory: false,
    page: 52,
    title: "Assemble a handheld radio",
    prompt:
      "Practically demonstrate, or discuss with your instructor, battery handling, radio handling, correct assembly and aerial fitting.",
    practical: true,
  },
];

const LEVELS = {
  pass: {
    label: "Pass – mandatory",
    chip:
      "bg-red-100 text-red-800 border-red-200",
    banner:
      "bg-red-700 text-white",
  },
  merit: {
    label: "Merit – optional",
    chip:
      "bg-yellow-100 text-yellow-900 border-yellow-300",
    banner:
      "bg-yellow-300 text-slate-950",
  },
  distinction: {
    label: "Distinction – optional",
    chip:
      "bg-blue-100 text-blue-800 border-blue-200",
    banner:
      "bg-raf-blue text-white",
  },
};

function SourcePage({
  task,
}) {
  const [open, setOpen] =
    useState(false);

  const pages =
    task.referencePages ||
    [task.page];

  const primaryPage =
    pages[0];

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
      <button
        type="button"
        onClick={() =>
          setOpen(
            (previous) =>
              !previous
          )
        }
        className="w-full flex items-center justify-between gap-3 p-4 text-left"
      >
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-raf-navy" />

          <div>
            <p className="font-bold text-raf-navy">
              Original workbook reference
            </p>

            <p className="text-xs text-slate-500 mt-0.5">
              Page{pages.length > 1 ? "s" : ""}{" "}
              {pages.join(", ")}
            </p>
          </div>
        </div>

        <ChevronDown
          className={[
            "w-5 h-5 text-slate-500 transition-transform",
            open
              ? "rotate-180"
              : "",
          ].join(" ")}
        />
      </button>

      {open && (
        <div className="border-t border-slate-200 bg-white">
          <div className="p-3 flex flex-wrap gap-2">
            {pages.map(
              (page) => (
                <a
                  key={page}
                  href={`${PDF_PATH}#page=${page}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-raf-navy"
                >
                  <ExternalLink size={15} />
                  Open page {page}
                </a>
              )
            )}

            <a
              href={PDF_PATH}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-raf-navy"
            >
              <BookOpen size={15} />
              Full logbook
            </a>
          </div>

          <div className="hidden md:block border-t border-slate-200">
            <iframe
              title={`First Class source page ${primaryPage}`}
              src={`${PDF_PATH}#page=${primaryPage}&zoom=page-width`}
              className="w-full h-[650px]"
            />
          </div>

          <div className="md:hidden px-4 pb-4 text-sm text-slate-500">
            On a smaller screen, use the
            "Open page" button above to
            view the source diagram or map.
          </div>
        </div>
      )}
    </div>
  );
}

function AnswerField({
  definition,
  value,
  onChange,
}) {
  const common =
    "mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-raf-blue focus:ring-2 focus:ring-blue-100";

  return (
    <label className="block">
      <span className="text-sm font-bold text-raf-navy">
        {definition.label}
      </span>

      {definition.multiline ? (
        <textarea
          rows={definition.rows}
          value={value || ""}
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          placeholder={
            definition.placeholder
          }
          className={`${common} resize-y`}
        />
      ) : (
        <input
          type="text"
          value={value || ""}
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          placeholder={
            definition.placeholder
          }
          className={common}
        />
      )}
    </label>
  );
}

function PracticalConfirmation({
  checked,
  onChange,
}) {
  return (
    <button
      type="button"
      onClick={() =>
        onChange(!checked)
      }
      className={[
        "w-full text-left rounded-xl border-2 p-4 transition",
        checked
          ? "border-emerald-400 bg-emerald-50"
          : "border-slate-200 bg-white hover:border-raf-blue",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        {checked ? (
          <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
        ) : (
          <Circle className="w-6 h-6 text-slate-400 shrink-0" />
        )}

        <div>
          <p className="font-bold text-raf-navy">
            I have completed this
            practical activity with an
            instructor.
          </p>

          <p className="text-sm text-slate-500 mt-1">
            This is only your
            confirmation. An instructor
            still needs to assess and
            approve the activity.
          </p>
        </div>
      </div>
    </button>
  );
}

function StatusChip({
  task,
  submittedAt,
  approved,
  review,
}) {
  const submitted =
    Boolean(submittedAt);

  const assessedAt =
    review?.assessed_at
      ? new Date(
          review.assessed_at
        ).getTime()
      : 0;

  const submittedTime =
    submittedAt
      ? new Date(
          submittedAt
        ).getTime()
      : 0;

  const returned =
    review?.review_outcome ===
      "returned" &&
    submittedTime <= assessedAt;

  if (approved) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">
        <CheckCircle2 size={13} />
        Staff approved
      </span>
    );
  }

  if (returned) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-100 px-2.5 py-1 text-xs font-bold text-red-800">
        <AlertTriangle size={13} />
        Returned for changes
      </span>
    );
  }

  if (submitted) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900">
        <ClipboardCheck size={13} />
        Awaiting staff check
      </span>
    );
  }

  const level =
    LEVELS[task.level];

  return (
    <span
      className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${level.chip}`}
    >
      {level.label}
    </span>
  );
}

function TaskCard({
  task,
  answers,
  practicalConfirmations,
  submittedAt,
  approved,
  review,
  updateAnswer,
  updatePractical,
  submitTask,
  submitting,
  error,
}) {
  const level =
    LEVELS[task.level];

  const submitted =
    Boolean(submittedAt);

  const assessedAt =
    review?.assessed_at
      ? new Date(
          review.assessed_at
        ).getTime()
      : 0;

  const submittedTime =
    submittedAt
      ? new Date(
          submittedAt
        ).getTime()
      : 0;

  const returned =
    review?.review_outcome ===
      "returned" &&
    submittedTime <= assessedAt;

  const resubmitted =
    review?.review_outcome ===
      "returned" &&
    submittedTime > assessedAt;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div
        className={`px-5 py-3 font-black text-sm uppercase tracking-wider ${level.banner}`}
      >
        {level.label}
      </div>

      <div className="p-5 md:p-7">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">
              {task.code}
            </p>

            <h2 className="text-2xl md:text-3xl font-black text-raf-navy mt-1">
              {task.title}
            </h2>

            <p className="text-slate-600 leading-relaxed mt-3">
              {task.prompt}
            </p>
          </div>

          <StatusChip
            task={task}
            submittedAt={submittedAt}
            approved={approved}
            review={review}
          />
        </div>

        <div className="mt-5">
          <SourcePage task={task} />
        </div>

        {task.fields?.length > 0 && (
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {task.fields.map(
              (definition) => (
                <AnswerField
                  key={definition.key}
                  definition={
                    definition
                  }
                  value={
                    answers?.[
                      definition.key
                    ]
                  }
                  onChange={(
                    value
                  ) =>
                    updateAnswer(
                      task.id,
                      definition.key,
                      value
                    )
                  }
                />
              )
            )}
          </div>
        )}

        {task.practical && (
          <div className="mt-6">
            <PracticalConfirmation
              checked={Boolean(
                practicalConfirmations?.[
                  task.id
                ]
              )}
              onChange={(
                value
              ) =>
                updatePractical(
                  task.id,
                  value
                )
              }
            />
          </div>
        )}

        {task.practical &&
          task.allowNotesWithPractical && (
            <div className="mt-4">
              <AnswerField
                definition={field(
                  "practicalNotes",
                  "Notes for your instructor",
                  { rows: 5 }
                )}
                value={
                  answers?.practicalNotes
                }
                onChange={(value) =>
                  updateAnswer(
                    task.id,
                    "practicalNotes",
                    value
                  )
                }
              />
            </div>
          )}

        {error && (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">
              {error}
            </p>
          </div>
        )}

        {review?.review_outcome && (
          <div
            className={[
              "mt-6 rounded-xl border p-4",
              approved
                ? "border-emerald-200 bg-emerald-50"
                : returned
                  ? "border-red-200 bg-red-50"
                  : "border-blue-200 bg-blue-50",
            ].join(" ")}
          >
            <div className="flex items-start gap-3">
              {approved ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-700 shrink-0" />
              ) : (
                <ClipboardCheck className="w-6 h-6 text-raf-blue shrink-0" />
              )}

              <div className="min-w-0">
                <p
                  className={[
                    "font-black",
                    approved
                      ? "text-emerald-900"
                      : returned
                        ? "text-red-900"
                        : "text-blue-900",
                  ].join(" ")}
                >
                  {approved
                    ? "Instructor assessment – approved"
                    : returned
                      ? "Instructor assessment – returned for changes"
                      : "Previous instructor feedback"}
                </p>

                <p className="text-sm text-slate-700 mt-1">
                  {review.assessor_rank}{" "}
                  {review.assessor_name}
                  {review.assessed_at
                    ? ` • ${new Intl.DateTimeFormat(
                        "en-GB",
                        {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      ).format(
                        new Date(
                          review.assessed_at
                        )
                      )}`
                    : ""}
                </p>

                {review.review_feedback && (
                  <div className="mt-3 rounded-lg bg-white/70 border border-black/5 p-3">
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-500">
                      Feedback
                    </p>

                    <p className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">
                      {review.review_feedback}
                    </p>
                  </div>
                )}

                {resubmitted && (
                  <p className="text-sm font-semibold text-blue-900 mt-3">
                    You have resubmitted this activity after the feedback above. It is awaiting another staff check.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-7 border-t border-slate-200 pt-5">
          {approved ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex gap-3">
              <FileCheck2 className="w-6 h-6 text-emerald-700 shrink-0" />

              <div>
                <p className="font-bold text-emerald-900">
                  Activity approved
                </p>

                <p className="text-sm text-emerald-800 mt-1">
                  An instructor has
                  marked this activity
                  complete.
                </p>
              </div>
            </div>
          ) : returned ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                <p className="font-bold text-red-900">
                  Changes requested
                </p>

                <p className="text-sm text-red-800 mt-1">
                  Read the instructor feedback above, make any changes needed, then resubmit this activity.
                </p>
              </div>

              <button
                type="button"
                onClick={() =>
                  submitTask(task)
                }
                disabled={submitting}
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-raf-red px-5 py-3 font-black text-white disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2
                    size={18}
                    className="animate-spin"
                  />
                ) : (
                  <ClipboardCheck
                    size={18}
                  />
                )}

                Resubmit for Staff Check
              </button>
            </div>
          ) : submitted ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <LockKeyhole className="w-6 h-6 text-amber-700 shrink-0" />

              <div>
                <p className="font-bold text-amber-950">
                  Ready for staff check
                </p>

                <p className="text-sm text-amber-900 mt-1">
                  Your work is saved.
                  You can still amend it
                  until an instructor
                  approves the activity.
                </p>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() =>
                submitTask(task)
              }
              disabled={submitting}
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-raf-red px-5 py-3 font-black text-white disabled:opacity-50"
            >
              {submitting ? (
                <Loader2
                  size={18}
                  className="animate-spin"
                />
              ) : (
                <ClipboardCheck
                  size={18}
                />
              )}

              Ready for Staff Check
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionOverview({
  section,
  tasks,
  submittedTasks,
  approvedTasks,
  onOpen,
}) {
  const Icon =
    section.icon;

  const mandatory =
    tasks.filter(
      (task) =>
        task.mandatory
    );

  const submitted =
    mandatory.filter(
      (task) =>
        submittedTasks[
          task.id
        ] ||
        approvedTasks[
          task.id
        ]
    ).length;

  const approved =
    mandatory.filter(
      (task) =>
        approvedTasks[
          task.id
        ]
    ).length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full bg-white border border-slate-200 rounded-xl p-5 text-left hover:border-raf-blue hover:shadow-sm transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-raf-navy text-white flex items-center justify-center shrink-0">
          <Icon size={24} />
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="font-black text-xl text-raf-navy">
            {section.title}
          </h3>

          <p className="text-sm text-slate-500 mt-1">
            {section.description}
          </p>

          <div className="mt-4 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-600 font-semibold">
              {mandatory.length} mandatory
            </span>

            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-900 font-semibold">
              {submitted} submitted
            </span>

            <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800 font-semibold">
              {approved} approved
            </span>

            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-blue-800 font-semibold">
              Source pages {section.pages}
            </span>
          </div>
        </div>

        <ChevronRight className="w-5 h-5 text-slate-400 shrink-0 mt-1" />
      </div>
    </button>
  );
}

export default function FirstClass() {
  const navigate =
    useNavigate();

  const {
    active,
    serviceNumber,
    activity,
    activityProgress,
    currentStep,
    sessionData,
    setCurrentStep,
    setSessionData,
    queueSave,
    saveState,
    updateModule,
    endSession,
    saving,
    ending,
    error,
  } = useTrainingSession();

  const [selectedSection, setSelectedSection] =
    useState(
      sessionData?.firstClassSection ||
        "flying"
    );

  const [selectedTaskId, setSelectedTaskId] =
    useState(
      FIRST_CLASS_TASKS.some(
        (task) =>
          task.id === currentStep
      )
        ? currentStep
        : FIRST_CLASS_TASKS[0].id
    );

  const [showOptional, setShowOptional] =
    useState(
      Boolean(
        sessionData?.firstClassShowOptional
      )
    );

  const [localError, setLocalError] =
    useState("");

  const [submittingTask, setSubmittingTask] =
    useState("");

  const answers =
    sessionData?.firstClassAnswers ||
    {};

  const submittedTasks =
    sessionData?.firstClassSubmitted ||
    {};

  const practicalConfirmations =
    sessionData?.firstClassPractical ||
    {};

  const approvedTasks =
    useMemo(() => {
      const result = {};

      for (const module of
        activityProgress?.modules ||
        []) {
        if (
          module?.status ===
          "complete"
        ) {
          result[
            module.module_id
          ] = true;
        }
      }

      return result;
    }, [activityProgress]);

  const reviews =
    useMemo(() => {
      const result = {};

      for (const module of
        activityProgress?.modules ||
        []) {
        if (
          module?.review_outcome
        ) {
          result[
            module.module_id
          ] = module;
        }
      }

      return result;
    }, [activityProgress]);

  useEffect(() => {
    if (
      !active ||
      !serviceNumber ||
      activity !== "firstclass"
    ) {
      navigate(
        "/training",
        { replace: true }
      );
    }
  }, [
    active,
    activity,
    navigate,
    serviceNumber,
  ]);

  useEffect(() => {
    if (
      currentStep &&
      FIRST_CLASS_TASKS.some(
        (task) =>
          task.id === currentStep
      )
    ) {
      const task =
        FIRST_CLASS_TASKS.find(
          (item) =>
            item.id === currentStep
        );

      setSelectedTaskId(
        currentStep
      );

      if (task?.section) {
        setSelectedSection(
          task.section
        );
      }
    }
  }, [currentStep]);

  const updateSession =
    useCallback(
      (updater) => {
        setSessionData(
          (previous) => {
            const next =
              updater(
                previous || {}
              );

            return next;
          }
        );

        queueSave();
      },
      [
        queueSave,
        setSessionData,
      ]
    );

  const updateAnswer =
    useCallback(
      (
        taskId,
        key,
        value
      ) => {
        updateSession(
          (previous) => ({
            ...previous,
            firstClassAnswers: {
              ...(
                previous.firstClassAnswers ||
                {}
              ),
              [taskId]: {
                ...(
                  previous.firstClassAnswers?.[
                    taskId
                  ] || {}
                ),
                [key]: value,
              },
            },
          })
        );
      },
      [updateSession]
    );

  const updatePractical =
    useCallback(
      (
        taskId,
        value
      ) => {
        updateSession(
          (previous) => ({
            ...previous,
            firstClassPractical: {
              ...(
                previous.firstClassPractical ||
                {}
              ),
              [taskId]: value,
            },
          })
        );
      },
      [updateSession]
    );

  const openTask =
    useCallback(
      async (task) => {
        setSelectedTaskId(
          task.id
        );

        setSelectedSection(
          task.section
        );

        setCurrentStep(
          task.id
        );

        setLocalError("");

        updateSession(
          (previous) => ({
            ...previous,
            firstClassSection:
              task.section,
          })
        );

        if (
          !approvedTasks[
            task.id
          ]
        ) {
          updateModule(
            task.id,
            "in_progress"
          ).catch(() => {});
        }
      },
      [
        approvedTasks,
        setCurrentStep,
        updateModule,
        updateSession,
      ]
    );

  const validateTask =
    useCallback(
      (task) => {
        if (
          task.fields?.length
        ) {
          const taskAnswers =
            answers[
              task.id
            ] || {};

          const missing =
            task.fields.find(
              (definition) =>
                !String(
                  taskAnswers[
                    definition.key
                  ] || ""
                ).trim()
            );

          if (missing) {
            return `Complete "${missing.label}" before submitting this activity.`;
          }
        }

        if (
          task.practical &&
          !practicalConfirmations[
            task.id
          ]
        ) {
          return "Confirm that you have completed the practical activity with an instructor.";
        }

        return "";
      },
      [
        answers,
        practicalConfirmations,
      ]
    );

  const submitTask =
    useCallback(
      async (task) => {
        setLocalError("");

        const validation =
          validateTask(task);

        if (validation) {
          setLocalError(
            validation
          );

          return;
        }

        setSubmittingTask(
          task.id
        );

        try {
          const nextSubmitted = {
            ...submittedTasks,
            [task.id]:
              new Date().toISOString(),
          };

          const nextData = {
            ...sessionData,
            firstClassSubmitted:
              nextSubmitted,
            firstClassSection:
              task.section,
          };

          setSessionData(
            nextData
          );

          await saveState(
            task.id,
            nextData
          );

          await updateModule(
            task.id,
            "in_progress"
          );
        } catch (err) {
          setLocalError(
            err?.response?.data?.detail ||
              err?.message ||
              "Unable to submit this activity."
          );
        } finally {
          setSubmittingTask("");
        }
      },
      [
        saveState,
        sessionData,
        setSessionData,
        submittedTasks,
        updateModule,
        validateTask,
      ]
    );

  const filteredTasks =
    useMemo(
      () =>
        FIRST_CLASS_TASKS.filter(
          (task) =>
            task.section ===
              selectedSection &&
            (showOptional ||
              task.mandatory)
        ),
      [
        selectedSection,
        showOptional,
      ]
    );

  useEffect(() => {
    if (
      !filteredTasks.some(
        (task) =>
          task.id ===
          selectedTaskId
      )
    ) {
      const next =
        filteredTasks[0];

      if (next) {
        setSelectedTaskId(
          next.id
        );
      }
    }
  }, [
    filteredTasks,
    selectedTaskId,
  ]);

  const selectedTask =
    FIRST_CLASS_TASKS.find(
      (task) =>
        task.id ===
        selectedTaskId
    ) ||
    filteredTasks[0] ||
    FIRST_CLASS_TASKS[0];

  const selectedIndex =
    filteredTasks.findIndex(
      (task) =>
        task.id ===
        selectedTask.id
    );

  const mandatoryTasks =
    FIRST_CLASS_TASKS.filter(
      (task) =>
        task.mandatory
    );

  const submittedMandatory =
    mandatoryTasks.filter(
      (task) =>
        submittedTasks[
          task.id
        ] ||
        approvedTasks[
          task.id
        ]
    ).length;

  const approvedMandatory =
    mandatoryTasks.filter(
      (task) =>
        approvedTasks[
          task.id
        ]
    ).length;

  const submissionPercent =
    mandatoryTasks.length
      ? Math.round(
          (submittedMandatory /
            mandatoryTasks.length) *
            100
        )
      : 0;

  const endAndSave =
    async () => {
      setLocalError("");

      try {
        await endSession();

        navigate(
          "/training",
          { replace: true }
        );
      } catch (err) {
        setLocalError(
          err?.response?.data?.detail ||
            err?.message ||
            "Unable to end the training session."
        );
      }
    };

  const toggleOptional = () => {
    const next =
      !showOptional;

    setShowOptional(next);

    updateSession(
      (previous) => ({
        ...previous,
        firstClassShowOptional:
          next,
      })
    );
  };

  if (
    !active ||
    !serviceNumber
  ) {
    return (
      <div className="min-h-screen bg-raf-navy text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-raf-navy text-white border-b-4 border-raf-red">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                <Plane size={23} />
              </div>

              <div>
                <p className="text-xs uppercase tracking-widest text-white/60 font-bold">
                  Classification Training
                </p>

                <h1 className="text-xl md:text-2xl font-black">
                  First Class Cadet
                </h1>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="rounded-lg bg-white/10 px-3 py-2">
                <p className="text-[10px] uppercase tracking-widest text-white/50">
                  Training record
                </p>

                <p className="font-bold tracking-wide">
                  {formatServiceNumber(
                    serviceNumber
                  )}
                </p>
              </div>

              <button
                type="button"
                onClick={endAndSave}
                disabled={
                  ending || saving
                }
                className="inline-flex items-center gap-2 rounded-lg bg-raf-red px-4 py-3 font-bold disabled:opacity-50"
              >
                {ending ? (
                  <Loader2
                    size={17}
                    className="animate-spin"
                  />
                ) : (
                  <Save size={17} />
                )}

                End Session & Save
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
        <div className="grid md:grid-cols-3 gap-3 mb-5">
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Mandatory activities
            </p>

            <p className="text-2xl font-black text-raf-navy mt-1">
              {mandatoryTasks.length}
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Submitted by cadet
            </p>

            <p className="text-2xl font-black text-amber-700 mt-1">
              {submittedMandatory}
              {" / "}
              {mandatoryTasks.length}
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Staff approved
            </p>

            <p className="text-2xl font-black text-emerald-700 mt-1">
              {approvedMandatory}
              {" / "}
              {mandatoryTasks.length}
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="font-bold text-raf-navy">
                Mandatory work submitted
              </p>

              <p className="text-sm text-slate-500 mt-1">
                Submission does not equal
                assessment approval.
              </p>
            </div>

            <p className="text-2xl font-black text-raf-navy">
              {submissionPercent}%
            </p>
          </div>

          <div className="mt-3 h-3 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-raf-blue transition-all duration-300"
              style={{
                width: `${submissionPercent}%`,
              }}
            />
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 flex gap-3">
          <ShieldCheck className="w-5 h-5 text-blue-800 shrink-0 mt-0.5" />

          <div className="text-sm text-blue-950">
            <p className="font-bold">
              Assessment remains an
              instructor decision.
            </p>

            <p className="mt-1">
              The electronic workbook
              records the cadet's work and
              practical confirmations.
              Activities remain awaiting
              staff approval until an
              instructor checks them.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-[300px_minmax(0,1fr)] gap-5">
          <aside className="lg:sticky lg:top-4 lg:self-start space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
                  First Class subjects
                </p>
              </div>

              <div className="p-2">
                {FIRST_CLASS_SECTIONS.map(
                  (section) => {
                    const Icon =
                      section.icon;

                    const activeSection =
                      selectedSection ===
                      section.id;

                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => {
                          setSelectedSection(
                            section.id
                          );

                          const nextTask =
                            FIRST_CLASS_TASKS.find(
                              (task) =>
                                task.section ===
                                  section.id &&
                                (showOptional ||
                                  task.mandatory)
                            );

                          if (nextTask) {
                            openTask(
                              nextTask
                            );
                          }
                        }}
                        className={[
                          "w-full rounded-lg p-3 text-left transition flex items-center gap-3",
                          activeSection
                            ? "bg-raf-navy text-white"
                            : "hover:bg-slate-50 text-slate-700",
                        ].join(" ")}
                      >
                        <Icon size={19} />

                        <span className="font-semibold leading-tight">
                          {section.title}
                        </span>
                      </button>
                    );
                  }
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={toggleOptional}
              className={[
                "w-full rounded-xl border p-4 text-left transition",
                showOptional
                  ? "border-yellow-300 bg-yellow-50"
                  : "border-slate-200 bg-white",
              ].join(" ")}
            >
              <p className="font-bold text-raf-navy">
                Optional Merit &
                Distinction
              </p>

              <p className="text-sm text-slate-500 mt-1">
                {showOptional
                  ? "Optional activities are shown."
                  : "Only mandatory Pass activities are shown."}
              </p>
            </button>

            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
                  Activities
                </p>
              </div>

              <div className="max-h-[52vh] overflow-y-auto p-2">
                {filteredTasks.map(
                  (task) => {
                    const selected =
                      task.id ===
                      selectedTask.id;

                    const approved =
                      approvedTasks[
                        task.id
                      ];

                    const submitted =
                      submittedTasks[
                        task.id
                      ];

                    return (
                      <button
                        key={task.id}
                        type="button"
                        onClick={() =>
                          openTask(task)
                        }
                        className={[
                          "w-full rounded-lg p-3 text-left transition",
                          selected
                            ? "bg-blue-50 border border-blue-200"
                            : "border border-transparent hover:bg-slate-50",
                        ].join(" ")}
                      >
                        <div className="flex items-start gap-2">
                          {approved ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                          ) : submitted ? (
                            <ClipboardCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                          ) : (
                            <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />
                          )}

                          <div className="min-w-0">
                            <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold">
                              {task.code}
                            </p>

                            <p className="text-sm font-semibold text-raf-navy leading-tight">
                              {task.title}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          </aside>

          <main className="min-w-0">
            <div className="mb-4 grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {FIRST_CLASS_SECTIONS.map(
                (section) => {
                  const sectionTasks =
                    FIRST_CLASS_TASKS.filter(
                      (task) =>
                        task.section ===
                        section.id
                    );

                  return (
                    <SectionOverview
                      key={section.id}
                      section={section}
                      tasks={sectionTasks}
                      submittedTasks={
                        submittedTasks
                      }
                      approvedTasks={
                        approvedTasks
                      }
                      onOpen={() => {
                        setSelectedSection(
                          section.id
                        );

                        const next =
                          sectionTasks.find(
                            (task) =>
                              showOptional ||
                              task.mandatory
                          );

                        if (next) {
                          openTask(next);
                        }
                      }}
                    />
                  );
                }
              )}
            </div>

            <TaskCard
              task={selectedTask}
              answers={
                answers[
                  selectedTask.id
                ] || {}
              }
              practicalConfirmations={
                practicalConfirmations
              }
              submittedAt={
                submittedTasks[
                  selectedTask.id
                ] || ""
              }
              approved={Boolean(
                approvedTasks[
                  selectedTask.id
                ]
              )}
              review={
                reviews[
                  selectedTask.id
                ] || null
              }
              updateAnswer={
                updateAnswer
              }
              updatePractical={
                updatePractical
              }
              submitTask={
                submitTask
              }
              submitting={
                submittingTask ===
                selectedTask.id
              }
              error={
                localError || error
              }
            />

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <button
                type="button"
                disabled={
                  selectedIndex <= 0
                }
                onClick={() => {
                  if (
                    selectedIndex > 0
                  ) {
                    openTask(
                      filteredTasks[
                        selectedIndex - 1
                      ]
                    );
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 font-bold text-raf-navy disabled:opacity-40"
              >
                <ChevronLeft size={18} />
                Previous
              </button>

              <button
                type="button"
                disabled={
                  selectedIndex < 0 ||
                  selectedIndex >=
                    filteredTasks.length -
                      1
                }
                onClick={() => {
                  if (
                    selectedIndex >=
                      0 &&
                    selectedIndex <
                      filteredTasks.length -
                        1
                  ) {
                    openTask(
                      filteredTasks[
                        selectedIndex + 1
                      ]
                    );
                  }
                }}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 font-bold text-raf-navy disabled:opacity-40"
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>

            {submittedMandatory ===
              mandatoryTasks.length && (
              <div className="mt-5 rounded-xl border border-amber-300 bg-amber-50 p-5">
                <div className="flex items-start gap-3">
                  <ClipboardCheck className="w-6 h-6 text-amber-700 shrink-0" />

                  <div>
                    <h3 className="font-black text-amber-950">
                      All mandatory work
                      has been submitted
                    </h3>

                    <p className="text-sm text-amber-900 mt-1">
                      The First Class
                      classification is
                      not yet complete.
                      Activities still
                      need appropriate
                      assessment and the
                      final logbook
                      verification
                      process.
                    </p>

                    <p className="text-xs text-amber-800 mt-3">
                      The source logbook
                      requires a cadet
                      declaration,
                      independent
                      internal
                      verification and
                      an update to BADER
                      SMS.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-5 bg-white border border-slate-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <FileCheck2 className="w-6 h-6 text-raf-navy shrink-0" />

                <div>
                  <h3 className="font-black text-raf-navy">
                    Final verification
                  </h3>

                  <p className="text-sm text-slate-600 mt-1">
                    The original First
                    Class logbook ends
                    with a cadet
                    declaration and an
                    independent internal
                    verification
                    declaration. This
                    website will add
                    that staff workflow
                    separately rather
                    than allowing a
                    cadet to
                    self-complete the
                    classification.
                  </p>

                  <a
                    href={`${PDF_PATH}#page=55`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-raf-blue hover:underline"
                  >
                    <ExternalLink size={15} />
                    View original
                    declaration page
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <button
                type="button"
                onClick={endAndSave}
                disabled={
                  ending || saving
                }
                className="w-full bg-raf-red text-white rounded-xl py-4 px-6 font-black text-lg flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {ending ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Saving session...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    End Session & Save
                  </>
                )}
              </button>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}